import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { highlightSnippet } from '../searchHighlight';

const STATUS_BADGE = { published: 'badge-published', draft: 'badge-draft', archived: 'badge-archived' };
const STATUS_LABEL = { published: 'Aktualna', draft: 'Szkic', archived: 'Archiwum' };

function flattenDepts(nodes, depth = 0, acc = []) {
  nodes.forEach(n => { acc.push({ ...n, depth }); if (n.children?.length) flattenDepts(n.children, depth + 1, acc); });
  return acc;
}

// AI Chat component — asks a question, gets an answer based on accessible procedures
function AiChat() {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]); // [{role: 'user'|'ai', text, sources}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();
  const nav = useNavigate();

  async function handleAsk(e) {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setMessages(m => [...m, { role: 'user', text: q }]);
    setQuestion('');
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/chat', { question: q });
      setMessages(m => [...m, { role: 'ai', text: r.data.answer, sources: r.data.sources || [] }]);
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd połączenia z AI. Spróbuj ponownie.');
    } finally { setLoading(false); setTimeout(() => inputRef.current?.focus(), 100); }
  }

  return (
    <div className="card" style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🤖</div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Zapytaj bazę wiedzy</div>
          <div className="text-muted">Zadaj pytanie — AI przeszuka procedury i odpowie</div>
        </div>
        {messages.length > 0 && (
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setMessages([])}>Wyczyść</button>
        )}
      </div>

      {/* Chat history */}
      {messages.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 420, overflowY: 'auto' }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              {m.role === 'ai' && (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, marginTop: 2 }}>🤖</div>
              )}
              <div style={{
                maxWidth: '80%',
                background: m.role === 'user' ? 'var(--primary)' : 'var(--gray-50)',
                color: m.role === 'user' ? 'white' : 'var(--gray-800)',
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '10px 14px',
                fontSize: 13.5,
                lineHeight: 1.6,
                border: m.role === 'ai' ? '1px solid var(--gray-200)' : 'none'
              }}>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 13.5 }}>{m.text}</div>
                {m.role === 'ai' && m.sources && m.sources.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--gray-200)' }}>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 4 }}>📄 Źródła:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {m.sources.map((s, si) => (
                        s.id ? (
                          <button key={si} onClick={() => nav(`/procedures/${s.id}`)} className="btn btn-secondary btn-sm" style={{ fontSize: 12 }}>
                            {s.title}
                          </button>
                        ) : (
                          <span key={si} style={{ fontSize: 12, color: 'var(--gray-500)', background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 4 }}>{s.title}</span>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {m.role === 'user' && (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'white', flexShrink: 0, marginTop: 2 }}>Ty</div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>🤖</div>
              <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: '16px 16px 16px 4px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="spinner"></div>
                <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>Szukam w procedurach...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: 'var(--danger)' }}>{error}</div>}

      <form onSubmit={handleAsk}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            className="form-input"
            style={{ flex: 1, fontSize: 14 }}
            placeholder="np. Co zrobić żeby dodać dilera na mapę?"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            disabled={loading}
          />
          <button className="btn btn-primary" type="submit" disabled={loading || !question.trim()} style={{ flexShrink: 0 }}>
            {loading ? '⏳' : '↵ Zapytaj'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [searchActive, setSearchActive] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();

  async function handleExportAll() {
    setExportingAll(true);
    try {
      const r = await api.get('/procedures/export-all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `procedury_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Błąd eksportu procedur');
    } finally {
      setExportingAll(false);
    }
  }

  useEffect(() => {
    api.get('/procedures').then(r => setRecent(r.data.slice(0, 6))).catch(() => {});
    api.get('/departments').then(r => setDepartments(r.data)).catch(() => {});
    if (isAdmin) api.get('/activity/stats').then(r => setStats(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (search.length >= 2) {
      api.get('/procedures/search-suggestions', { params: { q: search } }).then(r => { setSuggestions(r.data); setShowSugg(true); });
    } else { setSuggestions([]); setShowSugg(false); }
  }, [search]);

  async function handleSearch(e) {
    e.preventDefault();
    setShowSugg(false);
    const term = search.trim();
    if (!term) { setSearchResults(null); setSearchActive(''); return; }
    setSearching(true);
    setSearchActive(term);
    try {
      const r = await api.get('/procedures', { params: { search: term } });
      setSearchResults(r.data);
    } finally { setSearching(false); }
  }

  function clearSearch() { setSearch(''); setSearchActive(''); setSearchResults(null); }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Cześć, {user?.name?.split(' ')[0]} 👋</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4 }}>Czego szukasz?</p>
      </div>

      {/* AI Chat */}
      <AiChat />

      {/* Keyword search */}
      <div className="card" style={{ marginBottom: 28, padding: '16px 20px', position: 'relative' }}>
        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--gray-400)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              className="form-input"
              style={{ border: 'none', fontSize: 15, padding: '4px 0' }}
              placeholder="Szukaj słów kluczowych w dokumentach..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setShowSugg(false), 200)}
            />
            {searchResults !== null && <button type="button" className="btn-icon" onClick={clearSearch}>✕</button>}
            <button className="btn btn-secondary btn-sm" type="submit">Szukaj</button>
          </div>
        </form>
        {showSugg && suggestions.length > 0 && (
          <div className="search-suggestions" style={{ top: 'calc(100% - 4px)', left: 24, right: 24 }}>
            {suggestions.map(s => <div key={s.id} className="search-suggestions-item" onMouseDown={() => { nav(`/procedures/${s.id}`); setShowSugg(false); }}>{s.title}</div>)}
          </div>
        )}
        {searchResults !== null && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
            {searching ? (
              <div style={{ color: 'var(--gray-400)', fontSize: 13.5 }}>Szukam...</div>
            ) : searchResults.length === 0 ? (
              <div style={{ color: 'var(--gray-400)', fontSize: 13.5 }}>Brak wyników dla "{searchActive}"</div>
            ) : (
              <div>
                <div className="text-muted" style={{ marginBottom: 10 }}>{searchResults.length} {searchResults.length === 1 ? 'wynik' : 'wyników'} dla "{searchActive}"</div>
                {searchResults.map(p => (
                  <div key={p.id} onClick={() => nav(`/procedures/${p.id}`)} style={{ padding: '10px 0', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{p.title}</span>
                      <span className={`badge ${STATUS_BADGE[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                    </div>
                    {p.ai_summary && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>🤖 {p.ai_summary}</div>}
                    {p.search_snippet && (
                      <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4, background: 'var(--gray-50)', padding: '4px 8px', borderRadius: 4, lineHeight: 1.5 }}>
                        📄 {highlightSnippet(p.search_snippet, searchActive)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Department tiles */}
      {departments.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-900)' }}>Wybierz dział</h2>
            <button className="btn btn-secondary btn-sm" onClick={() => nav('/procedures')}>Wszystkie procedury</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {flattenDepts(departments).map(d => (
              <div key={d.id} className="card" style={{ cursor: 'pointer', padding: '16px 18px', transition: 'box-shadow .15s, transform .15s' }}
                onClick={() => nav(`/procedures?dept=${d.id}`)}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
                    {d.depth > 0 ? '↳' : '🗂️'}
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600, color: 'var(--gray-900)', fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                    <div className="text-muted">{d.procedure_count || 0} procedur</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && stats && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--primary)' }}>{stats.totalProcedures || 0}</div><div className="stat-label">Wszystkich procedur</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--success)' }}>{stats.byStatus?.approved || 0}</div><div className="stat-label">Zatwierdzonych</div></div>
          <div className="stat-card"><div className="stat-value">{stats.totalUsers || 0}</div><div className="stat-label">Użytkowników</div></div>
          <div className="stat-card"><div className="stat-value">{stats.totalDepartments || 0}</div><div className="stat-label">Działów</div></div>
        </div>
      )}

      {isAdmin && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleExportAll} disabled={exportingAll}>
            {exportingAll ? '⏳ Eksportowanie...' : '⬇ Eksportuj wszystkie procedury (JSON)'}
          </button>
          <button className="btn btn-secondary" onClick={() => nav('/backup')}>
            🗄 Zarządzaj kopiami zapasowymi
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Ostatnio zaktualizowane procedury</span>
          <button className="btn btn-secondary btn-sm" onClick={() => nav('/procedures')}>Zobacz wszystkie</button>
        </div>
        {recent.length === 0 ? <div className="empty-state"><p>Brak procedur</p></div> : (
          <div>
            {recent.map(p => (
              <div key={p.id} onClick={() => nav(`/procedures/${p.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>{(p.departments || []).map(d => d.name).join(' · ') || 'Bez działu'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span className={`badge ${STATUS_BADGE[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                  <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{new Date(p.updated_at).toLocaleDateString('pl-PL')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
